<template>
  <div class="card mb-2">
    <div class="card-body bg-secondary pt-2 pb-0 pl-1 pr-2">
      <b-form class="search-form" @submit.prevent="onSearch">
        <div class="d-flex">
          <div class="row align-items-center">
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="receipt_no"/>
            </div>
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="date" type="datepicker"/>
            </div>
            <div class="input-wrapper">
              <department-select-input-widget @select="onDepartmentSelect"
                                              :model="searchModel"
                                              :label="false"
                                              :options="{placeholder: $t('Department')}"
                                              attribute="department_uuid"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="enable_multiple_locations" type="select2" :items="yesNo"
                value-field="value" :label="false" title-hint :options="{placeholder: $t('Multi-Item Location')}"
                :append-question="$t('Filters receipts where multiple item locations are enabled.')"
                display-field="${item.key}"/>
            </div>
            <div class="input-wrapper" >
              <item-location-select-input-widget :model="searchModel" attribute="item_location_uuid" :label="false"
                                                 :placeholder="$t('Item Location')"/>
            </div>
            <div class="input-wrapper">
              <input-widget v-if="receiptContainerOptions && receiptContainerOptions.length"
                :model="searchModel" attribute="container_type" type="select2" :items="receiptContainerOptions"
                value-field="value" :label="false" title-hint :options="{placeholder: $t('Multi-Item Location Container')}"
                :append-question="$t('Enables searching receipts that include containers stored on item locations by container type.')"
                :display-field="(item) => item.value"/>
              <input-widget v-else :model="searchModel" attribute="container_type" :label="false"
                            type="text" :placeholder="$t('Multi-Item Location Container')" title-hint
                :append-question="$t('Enables searching receipts that include containers stored on item locations by container type.')"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="location_note" :label="false"
                            type="text" :placeholder="$t('Note')" title-hint
                :append-question="$t('Enables searching for multi-location receipts based on notes added under each location.')"/>
            </div>
            <div class="input-wrapper">
              <customer-select-input-widget @select="onCustomerSelect"
                                            :model="searchModel"
                                            :label="false" is-search
                                            :options="{placeholder: $t('Customer')}"
                                            attribute="customer_uuid"/>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget @select="onRecommendedByUserSelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Recommended By User')}"
                                        attribute="recommended_by_user_uuid" is-search/>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget @select="onRepresentativeByUserSelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Representative User')}"
                                        attribute="rep_user_uuid" is-search/>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget @select="onSpecialistSelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Specialist')}"
                                        attribute="specialist_uuid" is-search/>
            </div>
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="description"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="accounting_code" :label="false" :placeholder="true"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="receipt_contract_status" type="select2"
                            :items="contractStatuses"
                            value-field="key" :label="false" :options="{placeholder: $t('Consignment Status')}"
                            display-field="${item.text}">
              </input-widget>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="pre_receipt_contract_status" type="select2"
                            :items="contractStatuses"
                            value-field="key" :label="false" :options="{placeholder: $t('Pre-Consignment Status')}"
                            display-field="${item.text}">
              </input-widget>
            </div>
            <div class="input-wrapper" v-if="enableReceiptStatus">
              <input-widget :model="searchModel" attribute="status" value-field="value" display-field="${item.label}"
                            type="select2" :items="receiptStatuses" :label="false" :options="{placeholder: $t('Status')}"/>
            </div>

            <div class="input-wrapper">
              <customer-select-input-widget @select="onDropperSelect"
                                            :model="searchModel"
                                            :label="false" is-search
                                            :options="{placeholder: $t('Dropper')}"
                                            attribute="dropper_uuid"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="has_internal_images" type="checkbox"/>
            </div>

            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="has_attachments" type="checkbox"/>
            </div>
            <template v-if="receiptDynamicFieldsFilters.length > 0">
              <div class="input-wrapper" v-for="field in receiptDynamicFieldsFilters" :key="field.key">
                <input-widget :type="field.type" :label="getDynamicFieldFilterLabel(field)" :placeholder="field.label"
                              :model="searchModel" :attribute="field.key"/>
              </div>
            </template>
          </div>
          <div class="search-btn-wrapper">
            <b-button variant="primary" @click.prevent="onResetClick">
              <font-awesome-icon icon="undo" class="icon"/>
              {{ $t('Reset') }}
            </b-button>
            <b-button type="submit" variant="primary" class="ml-2">
              <font-awesome-icon icon="search" class="icon"/>
              {{ $t('Search') }}
            </b-button>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import DepartmentSelectInputWidget from "../../core/widgets/DepartmentSelectInputWidget";
  import {createNamespacedHelpers} from "vuex";

  import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
  import CustomerSelectInputWidget from "../../core/widgets/CustomerSelectInputWidget";
  import UserSelectInputWidget from "@/core/widgets/UserSelectInputWidget";
  import i18n from "../../shared/i18n";
  import ItemLocationSelectInputWidget from "@/core/widgets/ItemLocationSelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('receipts');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "ReceiptSearch",
    components: {
      ItemLocationSelectInputWidget,
      UserSelectInputWidget, CustomerSelectInputWidget, DepartmentSelectInputWidget, InputWidget},
    computed: {
      ...mapState({
        searchModel: state => state.tableData[state.tableSource].searchModel,
      }),
      ...globalMapState({
        enableReceiptStatus: state => state.enableReceiptStatus,
        receiptStatuses: state => state.receiptStatuses,
        yesNo: state => state.yesNo,
        receiptContainerOptions: state => state.receiptContainerOptions,
        dynamicFieldsFilters: state => state.dynamicFieldsFilters,
      }),
      receiptDynamicFieldsFilters() {
        let result = [];

        if (this.dynamicFieldsFilters) {
          let receiptDynamicFieldsFilters = this.dynamicFieldsFilters.receipts;
          if (receiptDynamicFieldsFilters && receiptDynamicFieldsFilters.length > 0) {
            result = receiptDynamicFieldsFilters;
          }
        }

        return result;
      }
    },
    data() {
      return {
        dateRange: {
          startDate: null,
          endDate: null
        },
        contractStatuses: [
          {key: 'draft', text: i18n.t('Draft')},
          {key: 'signed', text: i18n.t('Signed')},
          {key: 'not_generated', text: i18n.t('N/A')},
          {key: 'sent', text: i18n.t('Sent')},
        ]
      }
    },
    methods: {
      ...mapActions(['getTableData', 'updateSearchModel', 'resetTable', 'resetTableDataPaginationPage']),
      async onSearch() {
        await this.updateSearchModel(this.searchModel);
        this.resetTableDataPaginationPage();
        this.getTableData();
      },
      onResetClick() {
        this.resetTable();
      },
      async onDepartmentSelect(department) {
        this.updateSearchModel({department});
      },
      async onCustomerSelect(customer) {
        this.updateSearchModel({customer});
      },
      async onDropperSelect(dropper) {
        this.updateSearchModel({dropper});
      },
      async onRecommendedByUserSelect(recommended_by_user) {
        this.updateSearchModel({recommended_by_user});
      },
      async onRepresentativeByUserSelect(rep_user) {
        this.updateSearchModel({rep_user});
      },
      async onSpecialistSelect(specialist) {
        this.updateSearchModel({specialist});
      },
      getDynamicFieldFilterLabel(field) {
        if (field.type === 'checkbox') {
          return field.label;
        }
        return false;
      },
    },
  }
</script>

<style lang="scss" scoped>
</style>
